import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import styled from "styled-components";

import {
  BRICK_TERRACOTA,
  DAVYS_GREY,
  PLUM_PURPLE,
  PLUM_PURPLE_100,
  white,
} from "../colors";
import FlexBox from "../FlexBox";
import { Body2, H6 } from "../Headings";

const Container = styled(FlexBox)`
  flex-direction: column;
  padding: 1rem;
  background: ${white};
  box-shadow: 0px 0px 16px 4px rgba(33, 33, 33, 0.122);
`;

const ChatContainer = styled(FlexBox)`
  flex-direction: column;
  background-color: ${PLUM_PURPLE_100};
  border-radius: 0.5rem;
`;

const Wrapper = styled(FlexBox)`
  position: relative;
  overflow: hidden;
  width: 100%;
  border: 1px solid ${DAVYS_GREY[400]};
  border-radius: 0.5rem;
`;

const Textarea = styled.textarea`
  resize: none;
  border: none;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-family: inherit;
  flex: 1;

  min-height: 88px;
  max-height: 160px;
  padding: 0.5rem 3rem 0.5rem 0.5rem;

  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
    background: transparent;
  }

  color: ${DAVYS_GREY[1000]};

  &:disabled {
    background-color: ${white};
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  bottom: 0.25rem;
  right: 0.25rem;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ disabled }) =>
    disabled ? DAVYS_GREY[500] : BRICK_TERRACOTA[800]};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease;
`;

const ReloadButton = styled.button`
  border: none;
  border-radius: 0.25rem;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  background: ${PLUM_PURPLE[800]};
  color: ${white};
`;

const SafetyNet = styled(FlexBox)`
  background-color: ${PLUM_PURPLE[100]};
  align-items: ${({ $isStacked }) => ($isStacked ? "flex-start" : "center")};
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem 0.5rem 0 0;
  gap: 0.5rem 1rem;
`;

const StaticText = styled(H6)`
  white-space: nowrap;
  flex-shrink: 0;
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: ${PLUM_PURPLE[800]};
  gap: 0.5rem;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  background-color: ${PLUM_PURPLE[800]};
  border-radius: 50%;
  flex-shrink: 0;
`;

const AiChatInput = ({
  onSend = () => {},
  onPromptSent = () => {},
  onTypingActivity = () => {},
  aiResponding = false,
  setSelection = () => {},
  lastMessageInteractive,
  placeholder = "Ask anything about your session or client...",
  initialInput = "",
  onInitialInputApplied,
  disableChat = false,
  safetyNetContent = null,
  textareaRef,
}) => {
  const [input, setInput] = useState("");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [requiresReload, setRequiresReload] = useState(!navigator.onLine);
  const [isStacked, setStacked] = useState(false);

  const safetyNetRef = useRef(null);

  useLayoutEffect(() => {
    const el = safetyNetRef.current;
    if (!el) return;

    const update = () => {
      setStacked(el.scrollHeight > 38);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => observer.disconnect();
  }, [safetyNetContent, requiresReload]);

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      setRequiresReload(true);
    };

    const handleOnline = () => {
      setIsOffline(false);
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    if (!initialInput) return;

    setInput(initialInput);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();

        const length = initialInput.length;
        textareaRef.current.selectionStart = length;
        textareaRef.current.selectionEnd = length;
      }
    });

    if (onInitialInputApplied) {
      onInitialInputApplied();
    }
  }, [initialInput, onInitialInputApplied, textareaRef]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const MIN_HEIGHT = 88;
    const MAX_HEIGHT = 160;

    el.style.height = "auto";

    const scrollHeight = el.scrollHeight;
    const newHeight = Math.min(MAX_HEIGHT, Math.max(scrollHeight, MIN_HEIGHT));

    el.style.height = `${newHeight}px`;
    el.style.overflowY = scrollHeight > MAX_HEIGHT ? "scroll" : "hidden";
  }, [input, textareaRef]);

  useEffect(() => {
    if (
      !aiResponding &&
      !lastMessageInteractive &&
      !requiresReload &&
      textareaRef.current
    ) {
      textareaRef.current.focus();
    }
  }, [aiResponding, lastMessageInteractive, requiresReload, textareaRef]);

  const chatDisabled =
    lastMessageInteractive || aiResponding || disableChat || requiresReload;

  const handleSubmit = () => {
    if (chatDisabled || !input?.trim()) return;

    onSend(input);
    onPromptSent();
    setInput("");
  };

  const handleKeyDown = e => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    if (isMobile) return;

    if (e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        const el = textareaRef.current;
        if (!el) return;

        const start = el.selectionStart;
        const end = el.selectionEnd;
        const newValue = `${input.slice(0, start)}\n${input.slice(end)}`;

        setInput(newValue);
        setSelection(null);

        requestAnimationFrame(() => {
          el.selectionStart = start + 1;
          el.selectionEnd = start + 1;
        });

        return;
      }

      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Container>
      <ChatContainer>
        {(safetyNetContent || requiresReload) && (
          <SafetyNet ref={safetyNetRef} $isStacked={isStacked}>
            {requiresReload ? (
              <>
                <H6 color={PLUM_PURPLE[800]}>
                  {isOffline
                    ? "You are offline. Please check your internet connection and reload the page."
                    : "Connection was interrupted. Please reload the page before continuing."}
                </H6>

                <ReloadButton onClick={() => window.location.reload()}>
                  Reload
                </ReloadButton>
              </>
            ) : (
              <>
                {safetyNetContent?.clientId || safetyNetContent?.clientName ? (
                  <>
                    <StaticText color={PLUM_PURPLE[800]}>
                      You're chatting with AI.
                    </StaticText>

                    <ClientInfo>
                      {safetyNetContent?.clientName && (
                        <Body2 color={PLUM_PURPLE[800]} bold>
                          {safetyNetContent.clientName}
                        </Body2>
                      )}
                      {safetyNetContent?.clientName &&
                        safetyNetContent?.clientId && <Dot />}

                      {safetyNetContent?.clientId && (
                        <Body2 color={PLUM_PURPLE[800]}>
                          #{safetyNetContent.clientId}
                        </Body2>
                      )}
                    </ClientInfo>
                  </>
                ) : (
                  <H6 color={PLUM_PURPLE[800]}>
                    Always verify and interpret responses through your
                    expertise.
                  </H6>
                )}
              </>
            )}
          </SafetyNet>
        )}

        <Wrapper>
          <Textarea
            className="custom-scrollbar"
            ref={textareaRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              setSelection(null);
              onTypingActivity();
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={chatDisabled}
          />

          <SendButton
            onClick={handleSubmit}
            disabled={chatDisabled || !input.trim()}
          >
            <FaArrowUp color="white" />
          </SendButton>
        </Wrapper>
      </ChatContainer>
    </Container>
  );
};

export default AiChatInput;
